/**
 * Error Boundary Components
 *
 * Production-ready error boundaries with multiple variants:
 * - PageErrorBoundary: Full-page error handling
 * - SectionErrorBoundary: Section-level error handling
 * - ErrorBoundary: Default export (page-level)
 *
 * NOTE: This extends the base @tonnie/ui ErrorBoundary with app-specific features:
 * - Analytics integration via @tonnie/sdk (trackError)
 * - Multiple UI variants (page, section)
 * - Development-mode error details display
 *
 * Consider contributing analytics integration pattern to @tonnie/ui.
 */

import type { IErrorClient } from '@tonnie/observability';
import { AlertTriangle, Home, RefreshCw, RotateCcw, TonnieLogo } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Component, ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

interface IErrorInfo {
  componentStack: string;
}

interface IErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Observability error client (Sentry). When provided, reports caught errors to Sentry. */
  errorClient?: IErrorClient;
  /** Called when an error is caught - use for analytics integration */
  onError?: (error: Error, errorInfo: IErrorInfo) => void;
  /** Variant determines the UI style */
  variant?: 'page' | 'section';
  /** Custom title for the error message */
  title?: string;
  /** Whether to show error details in production */
  showDetails?: boolean;
}

interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: IErrorInfo | null;
}

// ============================================================================
// Error Logger (Analytics Integration)
// ============================================================================

import { getAnalyticsService } from '@tonnie/sdk';
import { cn } from '@tonnie/ui';

interface IErrorLogPayload {
  error: Error;
  errorInfo: IErrorInfo;
  timestamp: string;
  url: string;
  userAgent: string;
}

/**
 * Log error to analytics service.
 * Integrates with @tonnie/sdk analytics for error tracking.
 */
export function logErrorToAnalytics(payload: IErrorLogPayload): void {
  // Console logging for development
  if (import.meta.env.DEV) {
    console.error('[ErrorBoundary] Error caught:', {
      message: payload.error.message,
      stack: payload.error.stack,
      componentStack: payload.errorInfo.componentStack,
      timestamp: payload.timestamp,
      url: payload.url,
    });
  }

  // Get analytics service from SDK (singleton)
  const analyticsService = getAnalyticsService();

  // Track error via SDK analytics (integrates with Sentry/PostHog)
  analyticsService.trackError(payload.error, {
    componentStack: payload.errorInfo.componentStack,
    url: payload.url,
    userAgent: payload.userAgent,
    timestamp: payload.timestamp,
    source: 'error_boundary',
  });

  // Also track as event for analytics dashboards
  analyticsService.trackEvent('error_boundary_caught', {
    errorMessage: payload.error.message,
    errorName: payload.error.name,
    url: payload.url,
    timestamp: payload.timestamp,
  });
}

// ============================================================================
// Error Fallback Components
// ============================================================================

interface IErrorFallbackProps {
  error: Error | null;
  errorInfo: IErrorInfo | null;
  onRetry: () => void;
  onGoHome: () => void;
  variant: 'page' | 'section';
  title?: string;
  showDetails?: boolean;
}

export function ErrorFallback({
  error,
  errorInfo,
  onRetry,
  onGoHome,
  variant,
  title,
  showDetails = import.meta.env.MODE === 'development',
}: IErrorFallbackProps) {
  const { t } = useTranslation();
  const isPage = variant === 'page';

  // ── Section-level error (inline) ────────────────────────────────
  if (!isPage) {
    return (
      <div
        data-testid="app-error-fallback"
        data-variant="section"
        className="p-6 rounded-lg border border-destructive/20 bg-destructive/5"
      >
        <div className="flex flex-col items-center w-full">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-destructive/10 mb-3">
            <AlertTriangle className="w-5 h-5 text-destructive-accessible" />
          </div>
          <h2 className="text-base font-semibold text-foreground text-center mb-1">
            {title || t('error.sectionTitle')}
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-4">
            {t('error.sectionDescription')}
          </p>
          {showDetails && error && (
            <div className="w-full rounded-lg bg-muted/50 border border-border p-3 overflow-auto mb-4 max-h-32">
              {/* eslint-disable-next-line no-restricted-syntax -- developer-only details (`showDetails` defaults to dev builds), never shown to users */}
              <p className="text-xs font-mono text-destructive-accessible">{error.message}</p>
            </div>
          )}
          <button
            onClick={onRetry}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium',
              'border border-border bg-background shadow-sm hover:bg-muted transition-colors'
            )}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t('error.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ── Page-level error (full-screen) ──────────────────────────────
  return (
    <div
      data-testid="app-error-fallback"
      data-variant="page"
      className="min-h-screen bg-background flex flex-col items-center justify-center p-6"
    >
      <div className="w-full max-w-md flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-6 duration-500">
        {/* Brand logo */}
        <div className="mb-10 opacity-60">
          <TonnieLogo variant="symbol" size="md" />
        </div>

        {/* Error icon */}
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-2xl bg-destructive/10 flex items-center justify-center animate-in zoom-in-75 duration-500 delay-100">
            <AlertTriangle className="w-10 h-10 text-destructive-accessible" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground mb-2 animate-in fade-in duration-500 delay-150">
          {title || t('error.pageTitle')}
        </h1>

        {/* Description */}
        <p className="text-muted-foreground text-sm leading-relaxed mb-8 max-w-xs animate-in fade-in duration-500 delay-200">
          {t('error.pageDescription')}
        </p>

        {/* Error details (dev only) */}
        {showDetails && error && (
          <div className="w-full rounded-lg bg-muted/50 border border-border p-4 overflow-auto mb-8 max-h-40 text-left animate-in fade-in duration-500 delay-250">
            {/* eslint-disable-next-line no-restricted-syntax -- developer-only details (`showDetails` defaults to dev builds), never shown to users */}
            <p className="text-xs font-mono text-destructive-accessible mb-2">{error.message}</p>
            {errorInfo && (
              <pre className="text-2xs leading-relaxed text-muted-foreground whitespace-pre-wrap wrap-break-word">
                {errorInfo.componentStack}
              </pre>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 w-full max-w-xs animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
          <button
            onClick={onRetry}
            className={cn(
              'inline-flex items-center justify-center gap-2 w-full rounded-md px-4 py-2.5 text-sm font-medium',
              'bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors'
            )}
          >
            <RotateCcw className="w-4 h-4" />
            {t('error.tryAgain')}
          </button>

          <button
            onClick={onGoHome}
            className={cn(
              'inline-flex items-center justify-center gap-2 w-full rounded-md px-4 py-2.5 text-sm font-medium',
              'border border-border bg-background shadow-sm hover:bg-muted transition-colors'
            )}
          >
            <Home className="w-4 h-4" />
            {t('error.goHome')}
          </button>
        </div>

        {/* Decorative bottom element */}
        <div className="mt-12 flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in duration-700 delay-500">
          <div className="h-px w-8 bg-border" />
          <span>Tonnie</span>
          <div className="h-px w-8 bg-border" />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Error Boundary Class Component
// ============================================================================

/**
 * Detect the "browser-translator mutated the DOM under React" error.
 *
 * Primary defence is `shared/translation-shield.ts` (patches
 * `Node.prototype.insertBefore` / `removeChild` at startup so these
 * errors never reach React). This function is the BELT-AND-BRACES
 * second layer: if the shield missed something — new browser,
 * extension doing something weird, future React internals changing
 * — we detect the canonical error signature here and force a clean
 * remount of the subtree instead of showing the crash screen.
 *
 * Signature we're matching (Chromium, Safari, Firefox all emit
 * wording variants):
 *   • "Failed to execute 'insertBefore' on 'Node': The node before
 *      which the new node is to be inserted is not a child of this
 *      node."
 *   • "Failed to execute 'removeChild' on 'Node': The node to be
 *      removed is not a child of this node."
 */
function isTranslatorDomError(error: Error | null): boolean {
  if (!error) return false;
  if (error.name !== 'NotFoundError') return false;
  const msg = error.message || '';
  return /insertBefore|removeChild/.test(msg) && /not a child of this node/.test(msg);
}

export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  // Auto-recovery budget. If the translator keeps tearing the DOM
  // apart (shouldn't happen once the shield is installed, but just
  // in case a new browser quirk slips through), we allow one silent
  // remount; after that we fall through to the normal error UI to
  // avoid an infinite reload loop.
  private translatorRecoveryCount = 0;
  private translatorRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<IErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentWillUnmount(): void {
    if (this.translatorRecoveryTimer) {
      clearTimeout(this.translatorRecoveryTimer);
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const formattedErrorInfo: IErrorInfo = {
      componentStack: errorInfo.componentStack || '',
    };

    this.setState({ errorInfo: formattedErrorInfo });

    // ── Translator-induced DOM error: self-heal silently ───────────
    // If the shield missed a case and we caught the canonical
    // signature, schedule ONE auto-recovery. A microtask delay is
    // enough — the translator's next MutationObserver tick has
    // already run by then, so React can re-render cleanly.
    if (isTranslatorDomError(error) && this.translatorRecoveryCount === 0) {
      this.translatorRecoveryCount++;
      try {
        this.props.errorClient?.captureException(error, {
          extra: {
            componentStack: formattedErrorInfo.componentStack,
            url: typeof window !== 'undefined' ? window.location.href : '',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            source: 'error_boundary.translator_recovery',
            recoveryCount: this.translatorRecoveryCount,
          },
        });
      } catch (_) {
        /* keep going — don't let Sentry fail the recovery */
      }
      this.translatorRecoveryTimer = setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 50);
      return;
    }

    // Report to Sentry via observability error client (single source of truth per platform spec)
    try {
      this.props.errorClient?.captureException(error, {
        extra: {
          componentStack: formattedErrorInfo.componentStack,
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          source: 'error_boundary',
        },
      });
    } catch (_) {
      // Avoid breaking the boundary if Sentry fails
    }

    // Log to analytics (SDK trackError for existing analytics pipeline)
    logErrorToAnalytics({
      error,
      errorInfo: formattedErrorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, formattedErrorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback takes precedence
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          variant={this.props.variant || 'page'}
          title={this.props.title}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Convenience Wrapper Components
// ============================================================================

interface IPageErrorBoundaryProps {
  children: ReactNode;
  errorClient?: IErrorClient;
  onError?: (error: Error, errorInfo: IErrorInfo) => void;
  title?: string;
}

/**
 * Page-level error boundary with full-screen fallback UI.
 * Use this at route/page level to catch errors in entire pages.
 */
export function PageErrorBoundary({
  children,
  errorClient,
  onError,
  title,
}: IPageErrorBoundaryProps) {
  return (
    <ErrorBoundary variant="page" errorClient={errorClient} onError={onError} title={title}>
      {children}
    </ErrorBoundary>
  );
}

interface ISectionErrorBoundaryProps {
  children: ReactNode;
  errorClient?: IErrorClient;
  onError?: (error: Error, errorInfo: IErrorInfo) => void;
  title?: string;
  fallback?: ReactNode;
}

/**
 * Section-level error boundary with inline fallback UI.
 * Use this around individual sections/widgets that should fail independently.
 */
export function SectionErrorBoundary({
  children,
  errorClient,
  onError,
  title,
  fallback,
}: ISectionErrorBoundaryProps) {
  return (
    <ErrorBoundary
      variant="section"
      errorClient={errorClient}
      onError={onError}
      title={title}
      fallback={fallback}
    >
      {children}
    </ErrorBoundary>
  );
}

// Default export for backward compatibility
export default ErrorBoundary;
